import { totalLabel } from '../engine/costs.js';
import { planningSettings, currentReview } from '../engine/planning.js';
import { detailLevels, profile } from '../engine/standards.js';
import { useMemo, useState } from 'react';
import { usePlan } from '../store.js';
import { fmt } from '../engine/constants.js';
import { readPalette, hexA } from '../engine/render.js';
import { billOfMaterials } from '../engine/coverage.js';
import { formatArea, formatDensity, formatLength, measurementUnit } from '../engine/units.js';
import Icon from './Icon.jsx';

const MODES = [
  { k: 'grade', t: 'Grade' },
  { k: 'req', t: 'Target' },
  { k: 'redund', t: 'Overlap' },
  { k: 'px', t: 'Pixels' }
];

export function Legend({ store, ...props }) {
  usePlan(store);
  if (!store.cov) return null;
  return <CoverageLegend {...props} doc={store.doc} R={store.cov} />;
}

export function BuildingLegend({ levels, coverage, activeFloorId, ...props }) {
  const [keyId, setKeyId] = useState(null);
  const level = levels.find(f => f.id === keyId) || levels.find(f => f.id === activeFloorId && f.plan.areas.some(a => a.kind === 'floor')) || levels.find(f => f.plan.cams.length) || levels[0];
  if (!level) return null;
  const entry = coverage.get(level.id), R = entry?.result;
  const emptyMessage = entry?.status === 'empty' ? 'No floor or exterior area on this level.' : entry?.status === 'error' ? 'Coverage could not be calculated for this level. Use Retry above.' : entry?.status === 'off' ? 'Turn on Heat to calculate coverage.' : 'Calculating this level’s coverage…';
  return <CoverageLegend {...props} doc={level.plan} R={R} heading="Building coverage" className="building-legend"
    emptyMessage={emptyMessage}
    beforeKey={<div className="building-coverage-key"><label>Legend & figures for
      <select aria-label="Coverage key for level" value={level.id} onChange={e => setKeyId(e.target.value)}>{levels.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select>
    </label><div>{profile(level.plan).name}</div></div>}
    note="Heatmaps include cameras on other levels, using saved elevations and physical obstructions. Each level retains its own coverage requirements. Separate levels only changes the display." />;
}

function CoverageLegend({ doc, R, viewMode, setViewMode, showHeat, setShowHeat, showFov, setShowFov, open, onToggle, themeRev, heading = 'Coverage view', className = '', beforeKey, note, emptyMessage }) {
  const PAL = useMemo(() => readPalette(), [themeRev]);

  const st = R?.stats, cell = R?.cell;
  let rows = null;

  if (!R) {
    rows = <p className="hint" role="status">{emptyMessage}</p>;
  } else if (viewMode === 'grade') {
    rows = detailLevels(doc).map((_, i) => i).reverse().map(i => (
      <div className="row" key={i} title={i ? formatDensity(detailLevels(doc)[i].px, doc) : 'Below the lowest detail threshold'}>
        <span className="sw" style={{ background: i === 0 ? hexA(PAL.lv[0], 0.55) : PAL.lv[i] }} />
        {detailLevels(doc)[i].label}
        <span className="v">{formatArea(st.byLevel[i] * cell, doc, 0)}</span>
      </div>
    ));
  } else if (viewMode === 'req') {
    const metA = st.metPct / 100 * st.area;
    rows = (
      <>
        <div className="row"><span className="sw" style={{ background: PAL.lv[3] }} />Meets target<span className="v">{formatArea(metA, doc, 0)}</span></div>
        <div className="row"><span className="sw" style={{ background: hexA(PAL.lv[0], 0.6) }} />Falls short<span className="v">{formatArea(st.area - metA, doc, 0)}</span></div>
      </>
    );
  } else if (viewMode === 'redund') {
    let c0 = 0, c1 = 0, c2 = 0, c3 = 0;
    for (let i = 0; i < R.G.n; i++) {
      if (!R.G.inSite[i]) continue;
      const n = R.nMeet[i];
      if (n === 0) c0++; else if (n === 1) c1++; else if (n === 2) c2++; else c3++;
    }
    rows = (
      <>
        <div className="row"><span className="sw" style={{ background: PAL.lv[4] }} />Three or more<span className="v">{formatArea(c3 * cell, doc, 0)}</span></div>
        <div className="row"><span className="sw" style={{ background: PAL.lv[3] }} />Two cameras<span className="v">{formatArea(c2 * cell, doc, 0)}</span></div>
        <div className="row"><span className="sw" style={{ background: PAL.lv[1] }} />Single camera<span className="v">{formatArea(c1 * cell, doc, 0)}</span></div>
        <div className="row"><span className="sw" style={{ background: hexA(PAL.lv[0], 0.5) }} />None<span className="v">{formatArea(c0 * cell, doc, 0)}</span></div>
      </>
    );
  } else {
    const t = doc.opt.t;
    rows = (
      <>
        <div className="row">
          <span className="sw" style={{ background: `linear-gradient(90deg,${PAL.ramp[0]},${PAL.ramp[2]},${PAL.ramp[5]})` }} />
          {formatDensity(t.detect, doc, 0, { unit: false })}→{formatDensity(t.identify * 1.6, doc, 0)}
        </div>
        <div className="row" style={{ color: 'var(--text-3)', fontSize: 10 }}>
          Horizontal pixels across one {measurementUnit(doc, 'length')} of scene at face height.
        </div>
      </>
    );
  }

  return (
    <div className={'legend ' + className + (open ? '' : ' collapsed')}>
      <div className="legend-head">
        <h4>{heading}</h4>
        <button className="mini" type="button" onClick={onToggle} aria-expanded={open}
          title={open ? 'Collapse (L)' : 'Show the coverage key (L)'}>
          <Icon name={open ? 'chevron-up' : 'layers'} size={13} />
        </button>
      </div>
      <div className="legend-body">
        {beforeKey}
        <div style={{ display: 'flex', gap: 3, marginBottom: 8 }}>
          {MODES.map(m => (
            <button key={m.k} type="button"
              className={'btn sm' + (m.k === viewMode ? ' primary' : '')}
              style={{ flex: 1, padding: '0 4px' }}
              onClick={() => setViewMode(m.k)}>{m.t}</button>
          ))}
        </div>
        {rows}
        {R && <div className="foot">{formatArea(st.area, doc, 0)} {note ? 'on this level' : 'site'} · {formatLength(R.G.step, doc, 2)} samples</div>}
        {note && <p className="building-coverage-note">{note}</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 7 }}>
          <label className="check" style={{ margin: 0 }}>
            <input className="switch" type="checkbox" checked={showHeat} onChange={e => setShowHeat(e.target.checked)} />
            <span className="t" style={{ fontSize: 11 }}>Heat</span>
          </label>
          <label className="check" style={{ margin: 0 }}>
            <input className="switch" type="checkbox" checked={showFov} onChange={e => setShowFov(e.target.checked)} />
            <span className="t" style={{ fontSize: 11 }}>Cones</span>
          </label>
        </div>
      </div>
    </div>
  );
}

export function ScoreStrip({ store, open, onToggle }) {
  usePlan(store);
  const R = store.cov;
  if (!R) return null;
  const st = R.stats;
  const goal = planningSettings(store.doc).target;
  const checked = currentReview(store.doc)?.verification, failed = checked?.passed === false && !checked.nearMiss;
  const cls = v => (v >= goal ? 'good' : v >= goal * 0.85 ? 'warn' : 'bad');
  const { total, unknown } = billOfMaterials(store.doc);
  const cur = store.doc.opt.currency;

  return (
    <div className={'scorestrip' + (open ? '' : ' collapsed')}>
      <div className="cell"><div className="k">{failed ? 'Verification shortfall' : `Area grade · goal ${goal}%`}</div><div className={'v ' + (failed ? 'warn' : cls(st.metPct))}>{fmt(st.metPct, 1)}%</div></div>
      <div className="cell"><div className="k">Any coverage</div><div className={'v ' + cls(st.coveredPct)}>{fmt(st.coveredPct, 1)}%</div></div>
      <div className="cell"><div className="k">Blind</div>
        <div className={'v ' + (st.blindArea < 0.5 ? 'good' : st.blindArea > st.area * 0.05 ? 'bad' : 'warn')}>
          {formatArea(st.blindArea, store.doc)}
        </div>
      </div>
      <div className="cell"><div className="k">Cameras</div><div className="v">{store.doc.cams.length}</div></div>
      <div className="cell"><div className="k">Hardware</div><div className="v">{totalLabel(total, unknown, cur)}</div></div>
      <div className="cell tail">
        <button className="mini" type="button" onClick={onToggle} aria-expanded={open}
          title={open ? 'Collapse the summary' : 'Show the full summary'}>
          <Icon name="chevron-left" size={13} />
        </button>
      </div>
    </div>
  );
}
